/**
 * Operations for LoAF scopes around general JS work — event handlers, hooks, async tasks,
 * scenegraph operations, and so on.
 *
 * Scopes that measure the render-phase time of a specific React component belong in
 * `TrackedReactRenderOperation`, which is what `FigmaProfiler` / `withFigmaProfiler` accept for
 * their `trackAsOperation` prop.
 */
export enum TrackedOperation {
  AfterReactRender = 'AfterReactRender',
  ApplyAllChunks = 'ApplyAllChunks',
  ApplyDiff = 'ApplyDiff',
  ApplyFileToCurrentScene = 'ApplyFileToCurrentScene',
  AssetConsumptionMirror = 'AssetConsumptionMirror',
  AssetSyncObserverUpdate = 'AssetSyncObserverUpdate',
  AttemptActiveDocumentSave = 'AttemptActiveDocumentSave',
  BeforeReactRender = 'BeforeReactRender',
  BoxSelectionHitTest = 'BoxSelectionHitTest',
  CalculateVisiblePlayableMakeNodes = 'CalculateVisiblePlayableMakeNodes',
  ClickSelectionHitTest = 'ClickSelectionHitTest',
  ComputeLayerRowDerivedValues = 'ComputeLayerRowDerivedValues',
  ConvertToBitmapFromRenderTree = 'ConvertToBitmapFromRenderTree',
  Copy = 'Copy',
  CustomEffectCommandBatchApply = 'CustomEffectCommandBatchApply',
  CustomEffectMarkReady = 'CustomEffectMarkReady',
  DetachInstances = 'DetachInstances',
  DropCsvHandleLoadedFile = 'DropCsvHandleLoadedFile',
  DropHtmlHandleLoadedFile = 'DropHtmlHandleLoadedFile',
  DropKiwiHandleLoadedFile = 'DropKiwiHandleLoadedFile',
  DropPdfHandleLoadedFile = 'DropPdfHandleLoadedFile',
  DropRasterHandleLoadedFile = 'DropRasterHandleLoadedFile',
  DropSvgHandleLoadedFile = 'DropSvgHandleLoadedFile',
  DropVideoHandleLoadedFile = 'DropVideoHandleLoadedFile',
  DrawCanvasOverlayUI = 'DrawCanvasOverlayUI',
  DsaTrackedAction = 'DsaTrackedAction',
  DuplicateSelection = 'DuplicateSelection',
  ExportAllFramesToPdf = 'ExportAllFramesToPdf',
  ExportAsImageAsync = 'ExportAsImageAsync',
  ExportNode = 'ExportNode',
  ExportNodes = 'ExportNodes',
  ExportToPdf = 'ExportToPdf',
  FindHoveredNode = 'FindHoveredNode',
  FindOrCreateImageFromRawBytesAsync = 'FindOrCreateImageFromRawBytesAsync',
  FinishJoin = 'FinishJoin',
  FlushAutosave = 'FlushAutosave',
  FlushAsyncUpdaters = 'FlushAsyncUpdaters',
  ForEachVisibleDisabledShaderBadge = 'ForEachVisibleDisabledShaderBadge',
  FullscreenWasClosed = 'FullscreenWasClosed',
  FullscreenFrameEndToPaintFrame = 'FullscreenFrameEndToPaintFrame',
  GenerateCanvasRenderTree = 'GenerateCanvasRenderTree',
  GeneratePaintIcon = 'GeneratePaintIcon',
  GenerateVariableConsumptionInfo = 'GenerateVariableConsumptionInfo',
  GetAllAvailablePresets = 'GetAllAvailablePresets',
  GetAvailableModifiers = 'GetAvailableModifiers',
  GetCookieValueFromStorage = 'GetCookieValueFromStorage',
  GetExplicitModeNames = 'GetExplicitModeNames',
  GetFullscreenMenuItems = 'GetFullscreenMenuItems',
  GetGridRowRects = 'GetGridRowRects',
  GroupsAndStacksApplyLayoutFromDirtyNodes = 'GroupsAndStacksApplyLayoutFromDirtyNodes',
  GetMemoryUsage = 'GetMemoryUsage',
  GetOnboardingKeyElement = 'GetOnboardingKeyElement',
  GetStatsigExperimentImpl = 'GetStatsigExperimentImpl',
  GetSubscribedVariablesInfo = 'GetSubscribedVariablesInfo',
  GetUsedComponentsAndStateGroups = 'GetUsedComponentsAndStateGroups',
  HandleMouseDrag = 'HandleMouseDrag',
  HandleMouseMove = 'HandleMouseMove',
  HitTestWidgetNode = 'HitTestWidgetNode',
  HtmlToFigma = 'HtmlToFigma',
  InitializeFullscreen = 'InitializeFullscreen',
  InvalidateAllObjectsUsingImageResources = 'InvalidateAllObjectsUsingImageResources',
  InsertNodeAtPoint = 'InsertNodeAtPoint',
  InsertStateGroup = 'InsertStateGroup',
  JoinEnd = 'JoinEnd',
  JoinEndReconnect = 'JoinEndReconnect',
  JoinEndSkipReload = 'JoinEndSkipReload',
  LayoutEditDeleteSelection = 'LayoutEditDeleteSelection',
  LayoutEditModeHandlePasteHelper = 'LayoutEditModeHandlePasteHelper',
  LivegraphAiChatMessageConversion = 'LivegraphAiChatMessageConversion',
  LivegraphVariablesTranslation = 'LivegraphVariablesTranslation',
  LoadFigFileForPreview = 'LoadFigFileForPreview',
  LoadFontFromBuffer = 'LoadFontFromBuffer',
  LoadFromBuffer = 'LoadFromBuffer',
  LoadFromNewClipboardAPI = 'LoadFromNewClipboardAPI',
  MultiplayerSessionFlush = 'MultiplayerSessionFlush',
  NudgeForKeyEvent = 'NudgeForKeyEvent',
  PluginFindAll = 'PluginFindAll',
  PluginTimerCallback = 'PluginTimerCallback',
  UpdateSharedSymbol = 'UpdateSharedSymbol',
  ViewerMultiplayerReceiveWebSocketMessage = 'ViewerMultiplayerReceiveWebSocketMessage',
  NodeChanges = 'NodeChanges',
  NotifyListeners = 'NotifyListeners',
  PageChange = 'PageChange',
  ParseAndSetHoveredHyperlink = 'ParseAndSetHoveredHyperlink',
  Paste = 'Paste',
  PasteToReplace = 'PasteToReplace',
  Plugin = 'plugin',
  PerformVariableLocalizationFromPopup = 'PerformVariableLocalizationFromPopup',
  PreparePrototypeConnections = 'PreparePrototypeConnections',
  PreviewEditModeHandleCopy = 'PreviewEditModeHandleCopy',
  React = 'React',
  RedrawNow = 'RedrawNow',
  RenderArbitraryGlass = 'RenderArbitraryGlass',
  RenderBackgroundBlur = 'RenderBackgroundBlur',
  RenderCanvasTiles = 'RenderCanvasTiles',
  RenderRectangularGlass = 'RenderRectangularGlass',
  RenderThumbnailAsImage = 'RenderThumbnailAsImage',
  ReplaceFontsInSelection = 'ReplaceFontsInSelection',
  ReplaceSymbolBackingInstances = 'ReplaceSymbolBackingInstances',
  SaveToSystemClipboard = 'SaveToSystemClipboard',
  Search = 'Search',
  SearchPerformUpdate = 'SearchPerformUpdate',
  SelectPartitionedAssetsForPublishing = 'SelectPartitionedAssetsForPublishing',
  SelectionPropertiesFrameUpdate = 'SelectionPropertiesFrameUpdate',
  SetCursor = 'SetCursor',
  SetExplicitVariableModeForSelection = 'SetExplicitVariableModeForSelection',
  SetPlaygroundScene = 'SetPlaygroundScene',
  SetSearchScope = 'SetSearchScope',
  SetStackSizeOnSelection = 'SetStackSizeOnSelection',
  SortMenuItems = 'SortMenuItems',
  TextEditModeHandleCopy = 'TextEditModeHandleCopy',
  ThumbnailGenerateThumbnailForNode = 'ThumbnailGenerateThumbnailForNode',
  TriggerAction = 'TriggerAction',
  TryPastingNodesFromClipboardScene = 'TryPastingNodesFromClipboardScene',
  TypedPropAssignmentRow = 'TypedPropAssignmentRow',
  UnapplyAllChunks = 'UnapplyAllChunks',
  Undo = 'Undo',
  UpdateImageColorspaceConversions = 'UpdateImageColorspaceConversions',
  UpdateImmutableFramesAndLayoutDirtyGroupsAndStacks = 'UpdateImmutableFramesAndLayoutDirtyGroupsAndStacks',
  UpdateSelectionProperties = 'UpdateSelectionProperties',
  UpdateSharedStateGroup = 'UpdateSharedStateGroup',
  UpdateSharedStyle = 'UpdateSharedStyle',
  UpdateViewportInfo = 'UpdateViewportInfo',
  UpsertSharedStateGroup = 'UpsertSharedStateGroup',
  UseAllAnnotations = 'UseAllAnnotations',
  UseAllBrushes = 'UseAllBrushes',
  UsePreferencesApi = 'UsePreferencesApi',
  UseUpgradeHandler = 'UseUpgradeHandler',
  UseUsedAssetsSubscriber = 'UseUsedAssetsSubscriber',
  VariablesMirror = 'VariablesMirror',
}
